import React, { useEffect, useRef, useState } from 'react';
import pinPrompt from '../../utils/pinPrompt';
import { tmRequest } from '../../utils/tmApi';
import { CAUSE_OPTIONS, SHOP_ERROR_SUGGEST, TM_REASON_MIN, reasonOk } from '../../utils/tmPricing';

const causeLabel = (code) => (CAUSE_OPTIONS.find((c) => c.code === code) || {}).label || code || '—';

/**
 * "Charged or shop error?" for ONE defect of a pending T&M order (decision 1: billed by default;
 * decision 8: an Admin / Manager PIN; decision 13: the normal PIN prompt, auto-fill allowed).
 * Shown inline in the pricing window (never a second modal).
 *
 * mode: 'toggle' (the drill-down's Decide button: on a shop-error decision "Charged" is preselected;
 * otherwise — no decision, or a live CHARGED one — "shop error" is preselected only when the cause
 * suggests it, decision 1: billed by default), 'reconfirm' (the defect
 * changed after its shop-error decision: send it again), 'undo' (a shop-error decision no line
 * prices: charge it again). A reason of at least 5 visible characters either way; the cause is the
 * neutral station cause (optional: omitted keeps the one on record).
 *
 * PUT /sales_orders/{id}/tm_defect_decisions/{defect_id} {not_charged, reason, cause_code?, pin}.
 * onDone(data) after a 200 (the window re-reads the actuals and the defects, and drops the Preview).
 * locked: another request of the window is in flight (a Preview, a link write): Save waits for it.
 *
 * The window keys this dialog by defect + mode, so every opening starts from that defect's own state.
 * Closing it gives focus back to the button that opened it.
 */
export default function TmDefectDecisionDialog({ salesOrderId, defect, mode = 'toggle', onDone, onCancel, onBusy, locked = false }) {
  const d = defect || {};
  const live = d.decision || null;
  const nowShopError = !!(live && live.not_charged);
  const initialCause = (live && live.cause_code) || d.cause_code || '';
  const initialChoice = (() => {
    if (mode === 'reconfirm') return 'shop_error';
    if (mode === 'undo') return 'charged';
    if (nowShopError) return 'charged';
    return SHOP_ERROR_SUGGEST.includes(initialCause) ? 'shop_error' : 'charged';
  })();
  const [choice, setChoice] = useState(initialChoice);
  const [reason, setReason] = useState(mode === 'reconfirm' && live ? (live.reason || '') : '');
  const [cause, setCause] = useState(initialCause);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const headingRef = useRef(null);
  // The element that had focus when this dialog was rendered first (the button that opened it).
  const openerRef = useRef(typeof document !== 'undefined' ? document.activeElement : null);
  useEffect(() => {
    if (headingRef.current) {
      headingRef.current.focus();
      if (typeof headingRef.current.scrollIntoView === 'function') headingRef.current.scrollIntoView({ block: 'nearest' });
    }
    const opener = openerRef.current;
    return () => {
      if (opener && opener !== document.body && typeof opener.focus === 'function' && document.body.contains(opener)) opener.focus();
    };
  }, []);

  const suggests = SHOP_ERROR_SUGGEST.includes(cause);
  const ok = reasonOk(reason);
  const titleId = `tm-decision-title-${d.defect_id}`;

  const save = async () => {
    if (!ok || busy || locked) return;
    const notCharged = choice === 'shop_error';
    const pin = await pinPrompt(notCharged
      ? `Admin or Manager PIN to mark defect #${d.defect_id} "shop error: don't charge":`
      : `Admin or Manager PIN to charge defect #${d.defect_id}:`);
    if (!pin) return;
    const body = { not_charged: notCharged, reason: reason.trim(), pin };
    if (cause) body.cause_code = cause;
    setBusy(true);
    if (onBusy) onBusy(true);
    setError('');
    const r = await tmRequest('PUT', `/sales_orders/${salesOrderId}/tm_defect_decisions/${d.defect_id}`, body);
    setBusy(false);
    if (onBusy) onBusy(false);
    if (!r.ok) { setError(r.text); return; }
    if (onDone) onDone(r.data || {});
  };

  return (
    <div className="card border-primary mb-2" role="group" aria-labelledby={titleId} data-testid="tm-decision-dialog">
      <div className="card-body py-2 small">
        <h6 id={titleId} ref={headingRef} tabIndex={-1} className="mb-1">
          {mode === 'reconfirm' ? 'Re-confirm' : mode === 'undo' ? 'Undo' : 'Decide'}: defect #{d.defect_id}
          {d.station ? ` at ${d.station}` : ''}{d.n != null ? `, ${d.n} pc` : ''}
        </h6>
        <div className="text-muted mb-1">
          Now: {nowShopError ? "shop error — not charged" : 'charged (the default)'}
          {live && live.decided_by ? ` (by ${live.decided_by}${live.decided_at ? `, ${String(live.decided_at).slice(0, 16).replace('T', ' ')}` : ''})` : ''}
          {' · '}recorded cause: {causeLabel(d.cause_code)}
        </div>
        {mode === 'reconfirm' && (
          <div className="alert alert-warning py-1 mb-1">The defect changed after it was marked shop error. Send the decision again to confirm it for the defect as it is now.</div>
        )}
        <div className="d-flex flex-wrap gap-3 mb-1" role="radiogroup" aria-label="Charge this defect?">
          <label className="form-check-label">
            <input type="radio" className="form-check-input me-1" name={`tm-dec-${d.defect_id}`} checked={choice === 'charged'} onChange={() => setChoice('charged')} disabled={busy} />
            Charged (the customer pays for it)
          </label>
          <label className="form-check-label">
            <input type="radio" className="form-check-input me-1" name={`tm-dec-${d.defect_id}`} checked={choice === 'shop_error'} onChange={() => setChoice('shop_error')} disabled={busy} />
            Shop error: don&apos;t charge
          </label>
        </div>
        {suggests && <div className="text-info-emphasis mb-1"><i className="bi bi-lightbulb me-1"></i>The cause suggests a shop error (a hint, never the decision).</div>}
        <div className="row g-2 align-items-end">
          <div className="col-auto">
            <label className="form-label small mb-0" htmlFor={`tm-dec-cause-${d.defect_id}`}>Cause</label>
            <select id={`tm-dec-cause-${d.defect_id}`} className="form-select form-select-sm" value={cause} onChange={(e) => setCause(e.target.value)} disabled={busy}>
              <option value="">— keep the one on record —</option>
              {CAUSE_OPTIONS.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
            </select>
          </div>
          <div className="col">
            <label className="form-label small mb-0" htmlFor={`tm-dec-reason-${d.defect_id}`}>
              {choice === 'shop_error' ? 'Why is this a shop error?' : 'Why is it charged?'} (at least {TM_REASON_MIN} characters)
            </label>
            <input
              id={`tm-dec-reason-${d.defect_id}`}
              type="text"
              className={`form-control form-control-sm ${ok ? 'is-valid' : ''}`}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={busy}
            />
          </div>
        </div>
        {error && <div className="alert alert-danger py-1 mt-1 mb-0" role="alert">{error}</div>}
        <div className="d-flex gap-2 justify-content-end mt-2">
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="button" className="btn btn-sm btn-primary" onClick={save} disabled={!ok || busy || locked}>
            {busy ? 'Saving…' : 'Save with PIN'}
          </button>
        </div>
      </div>
    </div>
  );
}
